import { type RefObject, useEffect, useLayoutEffect, useRef, useState } from 'react';

export type FloatingCardSize = { width: number; height: number };
export type FloatingCardPosition = { x: number; y: number };
export type FloatingCardState = {
  position: FloatingCardPosition;
  size: FloatingCardSize;
};

export type ResizeCorner = 'se' | 'sw' | 'ne' | 'nw';

export type FloatingCardBounds = {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
};

export const getFloatingCardBounds = (
  boundsRef?: RefObject<HTMLElement | null>
): FloatingCardBounds | null => {
  const element = boundsRef?.current;
  if (!element) {
    return null;
  }

  const rect = element.getBoundingClientRect();
  return {
    minX: rect.left,
    minY: rect.top,
    maxX: rect.right,
    maxY: rect.bottom,
  };
};

const clampPosition = (
  x: number,
  y: number,
  w: number,
  h: number,
  bounds?: FloatingCardBounds | null
): FloatingCardPosition => {
  const minX = bounds?.minX ?? 0;
  const minY = bounds?.minY ?? 0;
  const maxX = bounds?.maxX ?? window.innerWidth;
  const maxY = bounds?.maxY ?? window.innerHeight;

  return {
    x: Math.min(Math.max(minX, x), Math.max(minX, maxX - w)),
    y: Math.min(Math.max(minY, y), Math.max(minY, maxY - h)),
  };
};

export type AnchorPlacement = 'auto' | 'above' | 'below';

type UseFloatingCardOptions = {
  minSize: FloatingCardSize;
  initialSize: FloatingCardSize;
  initialPosition?: FloatingCardPosition;
  initialState?: FloatingCardState;
  anchorRef?: RefObject<HTMLElement | null>;
  anchorPlacement?: AnchorPlacement;
  boundsRef?: RefObject<HTMLElement | null>;
  onUnmount?: (state: FloatingCardState) => void;
};

const ANCHOR_GAP = 8;
const MAX_ANCHOR_ATTEMPTS = 150;
const ANCHOR_STABLE_FRAMES = 3;
const ANCHOR_POSITION_TOLERANCE = 1;

const isAnchorMeasurable = (anchor: HTMLElement): boolean => {
  const rect = anchor.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
};

const isAnchorRectStable = (current: DOMRectReadOnly, previous: DOMRectReadOnly): boolean =>
  Math.abs(current.top - previous.top) <= ANCHOR_POSITION_TOLERANCE &&
  Math.abs(current.left - previous.left) <= ANCHOR_POSITION_TOLERANCE &&
  Math.abs(current.right - previous.right) <= ANCHOR_POSITION_TOLERANCE &&
  Math.abs(current.bottom - previous.bottom) <= ANCHOR_POSITION_TOLERANCE;

export const getAnchorPosition = (
  anchor: HTMLElement,
  cardWidth: number,
  cardHeight: number,
  placement: AnchorPlacement = 'auto',
  bounds?: FloatingCardBounds | null
): FloatingCardPosition => {
  const rect = anchor.getBoundingClientRect();
  const x = rect.right - cardWidth;
  const boundsMinY = bounds?.minY ?? 0;
  const boundsMaxY = bounds?.maxY ?? window.innerHeight;
  const spaceAbove = rect.top - boundsMinY - ANCHOR_GAP;
  const spaceBelow = boundsMaxY - rect.bottom - ANCHOR_GAP;

  let y: number;
  if (placement === 'above') {
    y = rect.top - cardHeight - ANCHOR_GAP;
    if (y < boundsMinY && spaceBelow >= spaceAbove) {
      y = rect.bottom + ANCHOR_GAP;
    }
  } else if (placement === 'below') {
    y = rect.bottom + ANCHOR_GAP;
    if (y + cardHeight > boundsMaxY && spaceAbove >= spaceBelow) {
      y = rect.top - cardHeight - ANCHOR_GAP;
    }
  } else if (spaceAbove >= cardHeight || spaceAbove >= spaceBelow) {
    y = rect.top - cardHeight - ANCHOR_GAP;
  } else {
    y = rect.bottom + ANCHOR_GAP;
  }

  return clampPosition(x, y, cardWidth, cardHeight, bounds);
};

export const useFloatingCard = ({
  minSize,
  initialSize,
  initialPosition,
  initialState,
  anchorRef,
  anchorPlacement = 'auto',
  boundsRef,
  onUnmount,
}: UseFloatingCardOptions) => {
  const [position, setPosition] = useState<FloatingCardPosition>(() => {
    const bounds = getFloatingCardBounds(boundsRef);
    if (initialState) {
      return clampPosition(
        initialState.position.x,
        initialState.position.y,
        initialState.size.width,
        initialState.size.height,
        bounds
      );
    }
    if (initialPosition) {
      return clampPosition(
        initialPosition.x,
        initialPosition.y,
        initialSize.width,
        initialSize.height,
        bounds
      );
    }
    if (anchorRef?.current) {
      return getAnchorPosition(
        anchorRef.current,
        initialSize.width,
        initialSize.height,
        anchorPlacement,
        bounds
      );
    }
    return clampPosition(
      window.innerWidth - initialSize.width - 24,
      window.innerHeight - initialSize.height - 80,
      initialSize.width,
      initialSize.height,
      bounds
    );
  });
  const [size, setSize] = useState<FloatingCardSize>(initialState?.size ?? initialSize);
  const [isAnchorPositioned, setIsAnchorPositioned] = useState(
    Boolean(initialState || initialPosition || !anchorRef)
  );

  const headerRef = useRef<HTMLDivElement>(null);
  const sizeRef = useRef(size);
  const positionRef = useRef(position);
  const hasUserPositionedRef = useRef(Boolean(initialState || initialPosition));

  useEffect(() => {
    sizeRef.current = size;
  }, [size]);

  useEffect(() => {
    positionRef.current = position;
  }, [position]);

  useLayoutEffect(() => {
    if (hasUserPositionedRef.current || !anchorRef) {
      if (!anchorRef) {
        setIsAnchorPositioned(true);
      }
      return;
    }

    let cancelled = false;
    let rafId = 0;
    let frame = 0;
    let stableFrames = 0;
    let lastRect: DOMRectReadOnly | null = null;

    const tick = () => {
      if (cancelled || hasUserPositionedRef.current) {
        return;
      }

      const anchor = anchorRef.current;
      if (!anchor || !isAnchorMeasurable(anchor)) {
        if (frame++ < MAX_ANCHOR_ATTEMPTS) {
          rafId = requestAnimationFrame(tick);
        } else {
          setIsAnchorPositioned(true);
        }
        return;
      }

      const rect = anchor.getBoundingClientRect();
      const anchoredPosition = getAnchorPosition(
        anchor,
        sizeRef.current.width,
        sizeRef.current.height,
        anchorPlacement,
        getFloatingCardBounds(boundsRef)
      );
      positionRef.current = anchoredPosition;
      setPosition(anchoredPosition);
      setIsAnchorPositioned(true);

      if (lastRect && isAnchorRectStable(rect, lastRect)) {
        stableFrames++;
      } else {
        stableFrames = 0;
      }
      lastRect = rect;

      if (stableFrames < ANCHOR_STABLE_FRAMES && frame++ < MAX_ANCHOR_ATTEMPTS) {
        rafId = requestAnimationFrame(tick);
      }
    };

    rafId = requestAnimationFrame(tick);

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
    };
  }, [anchorPlacement, anchorRef, boundsRef]);

  useEffect(() => {
    if (!boundsRef) {
      return;
    }

    const reclampToBounds = () => {
      const bounds = getFloatingCardBounds(boundsRef);
      if (!bounds) {
        return;
      }

      setPosition(currentPosition =>
        clampPosition(
          currentPosition.x,
          currentPosition.y,
          sizeRef.current.width,
          sizeRef.current.height,
          bounds
        )
      );
    };

    const boundsElement = boundsRef.current;
    if (!boundsElement) {
      return;
    }

    const resizeObserver = new ResizeObserver(reclampToBounds);
    resizeObserver.observe(boundsElement);
    window.addEventListener('resize', reclampToBounds);
    window.addEventListener('scroll', reclampToBounds, true);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', reclampToBounds);
      window.removeEventListener('scroll', reclampToBounds, true);
    };
  }, [boundsRef]);

  const dragRef = useRef<{
    startMouseX: number;
    startMouseY: number;
    startPosX: number;
    startPosY: number;
  } | null>(null);

  const resizeRef = useRef<{
    corner: ResizeCorner;
    startMouseX: number;
    startMouseY: number;
    startWidth: number;
    startHeight: number;
    startPosX: number;
    startPosY: number;
  } | null>(null);

  const mountedRef = useRef(true);
  const dragHandlersRef = useRef<{
    move: ((ev: MouseEvent) => void) | null;
    up: (() => void) | null;
  }>({ move: null, up: null });
  const resizeHandlersRef = useRef<{
    move: ((ev: MouseEvent) => void) | null;
    up: (() => void) | null;
  }>({ move: null, up: null });

  const latestStateRef = useRef<FloatingCardState>({ position, size });
  useEffect(() => {
    latestStateRef.current = { position, size };
  }, [position, size]);

  useEffect(() => {
    mountedRef.current = true;
    const dragHandlers = dragHandlersRef.current;
    const resizeHandlers = resizeHandlersRef.current;
    return () => {
      mountedRef.current = false;
      if (dragHandlers.move) {
        document.removeEventListener('mousemove', dragHandlers.move);
      }
      if (dragHandlers.up) {
        document.removeEventListener('mouseup', dragHandlers.up);
      }
      if (resizeHandlers.move) {
        document.removeEventListener('mousemove', resizeHandlers.move);
      }
      if (resizeHandlers.up) {
        document.removeEventListener('mouseup', resizeHandlers.up);
      }
      onUnmount?.(latestStateRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleHeaderMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) {
      return;
    }
    e.preventDefault();
    hasUserPositionedRef.current = true;
    dragRef.current = {
      startMouseX: e.clientX,
      startMouseY: e.clientY,
      startPosX: positionRef.current.x,
      startPosY: positionRef.current.y,
    };

    const handleMouseMove = (ev: MouseEvent) => {
      if (!dragRef.current || !mountedRef.current) {
        return;
      }
      setPosition(
        clampPosition(
          dragRef.current.startPosX + (ev.clientX - dragRef.current.startMouseX),
          dragRef.current.startPosY + (ev.clientY - dragRef.current.startMouseY),
          sizeRef.current.width,
          sizeRef.current.height,
          getFloatingCardBounds(boundsRef)
        )
      );
    };

    const handleMouseUp = () => {
      dragRef.current = null;
      dragHandlersRef.current.move = null;
      dragHandlersRef.current.up = null;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    dragHandlersRef.current.move = handleMouseMove;
    dragHandlersRef.current.up = handleMouseUp;
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleResizeMouseDown = (e: React.MouseEvent, corner: ResizeCorner) => {
    if (e.button !== 0) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    hasUserPositionedRef.current = true;
    resizeRef.current = {
      corner,
      startMouseX: e.clientX,
      startMouseY: e.clientY,
      startWidth: sizeRef.current.width,
      startHeight: sizeRef.current.height,
      startPosX: positionRef.current.x,
      startPosY: positionRef.current.y,
    };

    const handleMouseMove = (ev: MouseEvent) => {
      if (!resizeRef.current || !mountedRef.current) {
        return;
      }
      const {
        corner: c,
        startMouseX,
        startMouseY,
        startWidth,
        startHeight,
        startPosX,
        startPosY,
      } = resizeRef.current;
      const dx = ev.clientX - startMouseX;
      const dy = ev.clientY - startMouseY;

      let newWidth = startWidth;
      let newHeight = startHeight;
      let newX = startPosX;
      let newY = startPosY;

      const bounds = getFloatingCardBounds(boundsRef);
      const boundsMinX = bounds?.minX ?? 0;
      const boundsMinY = bounds?.minY ?? 0;
      const boundsMaxX = bounds?.maxX ?? window.innerWidth;
      const boundsMaxY = bounds?.maxY ?? window.innerHeight;

      const maxWidthFromRight = boundsMaxX - startPosX;
      const maxHeightFromBottom = boundsMaxY - startPosY;
      const maxWidthFromLeft = startPosX + startWidth - boundsMinX;
      const maxHeightFromTop = startPosY + startHeight - boundsMinY;

      if (c === 'se') {
        newWidth = Math.min(Math.max(minSize.width, startWidth + dx), maxWidthFromRight);
        newHeight = Math.min(Math.max(minSize.height, startHeight + dy), maxHeightFromBottom);
      } else if (c === 'sw') {
        const clampedWidth = Math.min(Math.max(minSize.width, startWidth - dx), maxWidthFromLeft);
        newWidth = clampedWidth;
        newX = startPosX + (startWidth - clampedWidth);
        newHeight = Math.min(Math.max(minSize.height, startHeight + dy), maxHeightFromBottom);
      } else if (c === 'ne') {
        newWidth = Math.min(Math.max(minSize.width, startWidth + dx), maxWidthFromRight);
        const clampedHeight = Math.min(
          Math.max(minSize.height, startHeight - dy),
          maxHeightFromTop
        );
        newHeight = clampedHeight;
        newY = startPosY + (startHeight - clampedHeight);
      } else if (c === 'nw') {
        const clampedWidth = Math.min(Math.max(minSize.width, startWidth - dx), maxWidthFromLeft);
        newWidth = clampedWidth;
        newX = startPosX + (startWidth - clampedWidth);
        const clampedHeight = Math.min(
          Math.max(minSize.height, startHeight - dy),
          maxHeightFromTop
        );
        newHeight = clampedHeight;
        newY = startPosY + (startHeight - clampedHeight);
      }

      setSize({ width: newWidth, height: newHeight });
      setPosition(clampPosition(newX, newY, newWidth, newHeight, bounds));
    };

    const handleMouseUp = () => {
      resizeRef.current = null;
      resizeHandlersRef.current.move = null;
      resizeHandlersRef.current.up = null;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    resizeHandlersRef.current.move = handleMouseMove;
    resizeHandlersRef.current.up = handleMouseUp;
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const resizeCursors: Record<ResizeCorner, string> = {
    se: 'se-resize',
    sw: 'sw-resize',
    ne: 'ne-resize',
    nw: 'nw-resize',
  };

  const getCornerHandleProps = (corner: ResizeCorner) => ({
    ...(corner === 'se' ? { $right: 0, $bottom: 0 } : {}),
    ...(corner === 'sw' ? { $left: 0, $bottom: 0 } : {}),
    ...(corner === 'ne' ? { $right: 0, $top: 0 } : {}),
    ...(corner === 'nw' ? { $left: 0, $top: 0 } : {}),
  });

  return {
    position,
    size,
    setPosition,
    setSize,
    headerRef,
    isAnchorPositioned,
    onHeaderMouseDown: handleHeaderMouseDown,
    onResizeMouseDown: handleResizeMouseDown,
    resizeCursors,
    getCornerHandleProps,
  };
};
